import { Country, State } from 'country-state-city'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

const FALLBACK_COUNTRY_CODE = 'IN'
const MOBILE_NUMBER_TOTAL_MAX_DIGITS = 15
export const MOBILE_NUMBER_LOCAL_MAX_DIGITS = 14
const GEO_LOOKUP_TIMEOUT_MS = 3000
const GEO_RATE_LIMIT_BLOCK_MS = 10 * 60 * 1000
let geoLookupBlockedUntil = 0

const countryOptions = Country.getAllCountries()
  .map((country) => ({
    code: country.isoCode,
    name: country.name,
    phoneCode: (country.phonecode || '').replace(/\D/g, ''),
    flag: country.flag || '',
  }))
  .sort((a, b) => a.name.localeCompare(b.name))

const countryByCode = new Map(
  countryOptions.map((country) => [country.code.toUpperCase(), country]),
)

const countryByName = new Map(
  countryOptions.map((country) => [country.name.toLowerCase(), country]),
)

const uniquePhoneCodes = new Set()

const phoneCodeOptions = countryOptions
  .filter((country) => country.phoneCode)
  .map((country) => ({
    value: country.phoneCode,
    label: `${country.name} (+${country.phoneCode})`,
  }))
  .filter((entry) => {
    if (uniquePhoneCodes.has(entry.value)) {
      return false
    }
    uniquePhoneCodes.add(entry.value)
    return true
  })

const defaultStateOptions = [
  { value: 'NOT_APPLICABLE', label: 'Not Applicable' },
  { value: 'OTHER', label: 'Other / Not Listed' },
]

const allPhoneCodesDesc = [...uniquePhoneCodes].sort(
  (a, b) => b.length - a.length,
)

export const COUNTRY_OPTIONS = countryOptions
export const PHONE_CODE_OPTIONS = phoneCodeOptions

export const resolveCountryCode = (countryValue) => {
  if (!countryValue || !countryValue.trim()) {
    return FALLBACK_COUNTRY_CODE
  }

  const normalized = countryValue.trim()
  const upper = normalized.toUpperCase()
  if (countryByCode.has(upper)) {
    return upper
  }

  const byName = countryByName.get(normalized.toLowerCase())
  if (byName) {
    return byName.code
  }

  return FALLBACK_COUNTRY_CODE
}

export const getCountryPhoneCode = (countryCode) => {
  const resolvedCountryCode = resolveCountryCode(countryCode)
  return countryByCode.get(resolvedCountryCode)?.phoneCode || '91'
}

export const getStateOptions = (countryCode, currentState) => {
  const resolvedCountryCode = resolveCountryCode(countryCode)
  const states = State.getStatesOfCountry(resolvedCountryCode) || []
  const mappedStates = states
    .map((state) => ({ value: state.name, label: state.name }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const effectiveStates =
    mappedStates.length > 0 ? mappedStates : defaultStateOptions

  if (
    currentState &&
    !effectiveStates.some((state) => state.value === currentState)
  ) {
    return [{ value: currentState, label: currentState }, ...effectiveStates]
  }

  return effectiveStates
}

export const normalizeLocalMobileNumber = (value) =>
  (value || '').replace(/\D/g, '')

const normalizePhoneCode = (value) => (value || '').replace(/\D/g, '')

export const validateAndFormatMobileNumber = ({
  phoneCode,
  localNumber,
  fallbackCountryCode,
}) => {
  const normalizedCode = normalizePhoneCode(phoneCode)
  const normalizedLocal = normalizeLocalMobileNumber(localNumber)

  if (!normalizedLocal) {
    return {
      isValid: false,
      error: 'Mobile number is required',
      storedNumber: '',
    }
  }

  if (!normalizedCode) {
    return {
      isValid: false,
      error: 'Country code is required',
      storedNumber: '',
    }
  }

  const resolvedCountryCode = fallbackCountryCode
    ? resolveCountryCode(fallbackCountryCode)
    : undefined
  const fullNumber = `+${normalizedCode}${normalizedLocal}`
  let parsed = parsePhoneNumberFromString(fullNumber, resolvedCountryCode)

  // User might paste a full number (including country code) in the local
  // field. Accept it if it resolves to the same selected country code.
  if (
    (!parsed || !parsed.isValid()) &&
    normalizedLocal.startsWith(normalizedCode)
  ) {
    const localAsFullParsed = parsePhoneNumberFromString(`+${normalizedLocal}`)
    if (
      localAsFullParsed &&
      localAsFullParsed.isValid() &&
      localAsFullParsed.countryCallingCode === normalizedCode
    ) {
      parsed = localAsFullParsed
    }
  }

  // Fallback parse for national input formats (for example, local numbers with a
  // national prefix entered in the local-number field).
  if ((!parsed || !parsed.isValid()) && resolvedCountryCode) {
    const nationalParsed = parsePhoneNumberFromString(
      normalizedLocal,
      resolvedCountryCode,
    )

    if (
      nationalParsed &&
      nationalParsed.isValid() &&
      nationalParsed.countryCallingCode === normalizedCode
    ) {
      parsed = nationalParsed
    }
  }

  if (!parsed || !parsed.isValid()) {
    return {
      isValid: false,
      error: 'Please enter a valid mobile number and select the matching country code',
      storedNumber: '',
    }
  }

  const e164 = parsed.number || fullNumber
  const storedNumber = e164.replace(/\D/g, '')

  if (!storedNumber || storedNumber.length > MOBILE_NUMBER_TOTAL_MAX_DIGITS) {
    return {
      isValid: false,
      error: `Mobile number must include country code and be up to ${MOBILE_NUMBER_TOTAL_MAX_DIGITS} digits`,
      storedNumber: '',
    }
  }

  return {
    isValid: true,
    error: null,
    storedNumber,
  }
}

const fetchJsonWithTimeout = async (url) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), GEO_LOOKUP_TIMEOUT_MS)

  try {
    return await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
  } finally {
    clearTimeout(timeoutId)
  }
}

const normalizeGeoHint = ({ countryCode, city, region, source }) => {
  const normalizedCountry = (countryCode || '').toString().trim().toUpperCase()
  const normalizedCity = (city || '').toString().trim()
  const normalizedRegion = (region || '').toString().trim()

  if (
    normalizedCountry.length !== 2 &&
    !normalizedCity &&
    !normalizedRegion
  ) {
    return null
  }

  return {
    countryCode: normalizedCountry.length === 2 ? normalizedCountry : '',
    city: normalizedCity,
    region: normalizedRegion,
    source,
  }
}

const parseRetryAfterMs = (response) => {
  const retryAfterRaw = response?.headers?.get?.('retry-after')
  if (!retryAfterRaw) {
    return GEO_RATE_LIMIT_BLOCK_MS
  }

  const seconds = Number(retryAfterRaw)
  if (Number.isFinite(seconds) && seconds > 0) {
    return seconds * 1000
  }

  const retryDateMs = Date.parse(retryAfterRaw)
  if (!Number.isNaN(retryDateMs)) {
    return Math.max(0, retryDateMs - Date.now())
  }

  return GEO_RATE_LIMIT_BLOCK_MS
}

const tryIpApiCo = async () => {
  const response = await fetchJsonWithTimeout('https://ipapi.co/json/')
  if (response.status === 429) {
    return {
      rateLimited: true,
      retryAfterMs: parseRetryAfterMs(response),
      hint: null,
    }
  }
  if (!response.ok) {
    return { rateLimited: false, hint: null }
  }

  const data = await response.json()
  if (data?.error) {
    return { rateLimited: false, hint: null }
  }

  return {
    rateLimited: false,
    hint: normalizeGeoHint({
      countryCode: data?.country_code,
      city: data?.city,
      region: data?.region,
      source: 'ipapi.co',
    }),
  }
}

const tryIpWhoIs = async () => {
  const response = await fetchJsonWithTimeout('https://ipwho.is/')
  if (response.status === 429) {
    return {
      rateLimited: true,
      retryAfterMs: parseRetryAfterMs(response),
      hint: null,
    }
  }
  if (!response.ok) {
    return { rateLimited: false, hint: null }
  }

  const data = await response.json()
  if (!data?.success) {
    return { rateLimited: false, hint: null }
  }

  return {
    rateLimited: false,
    hint: normalizeGeoHint({
      countryCode: data?.country_code,
      city: data?.city,
      region: data?.region,
      source: 'ipwho.is',
    }),
  }
}

const tryIpInfo = async () => {
  const response = await fetchJsonWithTimeout('https://ipinfo.io/json')
  if (response.status === 429) {
    return {
      rateLimited: true,
      retryAfterMs: parseRetryAfterMs(response),
      hint: null,
    }
  }
  if (!response.ok) {
    return { rateLimited: false, hint: null }
  }

  const data = await response.json()
  return {
    rateLimited: false,
    hint: normalizeGeoHint({
      countryCode: data?.country,
      city: data?.city,
      region: data?.region,
      source: 'ipinfo.io',
    }),
  }
}

export const fetchIpLocationHints = async () => {
  if (Date.now() < geoLookupBlockedUntil) {
    return null
  }

  const providers = [tryIpApiCo, tryIpWhoIs, tryIpInfo]
  let suggestedBackoffMs = 0

  for (const provider of providers) {
    try {
      const result = await provider()
      if (result?.hint) {
        return result.hint
      }
      if (result?.rateLimited) {
        suggestedBackoffMs = Math.max(
          suggestedBackoffMs,
          result.retryAfterMs || GEO_RATE_LIMIT_BLOCK_MS,
        )
      }
    } catch (error) {
      // Non-blocking fallback; try next provider.
    }
  }

  if (suggestedBackoffMs > 0) {
    geoLookupBlockedUntil = Date.now() + suggestedBackoffMs
  }

  return null
}

export const splitStoredMobileNumber = (storedNumber, fallbackPhoneCode) => {
  const normalizedStored = normalizeLocalMobileNumber(storedNumber)
  const normalizedFallback = normalizePhoneCode(fallbackPhoneCode)

  if (!normalizedStored) {
    return { phoneCode: normalizedFallback || '91', localNumber: '' }
  }

  const parsed = parsePhoneNumberFromString(`+${normalizedStored}`)
  if (parsed && parsed.isValid()) {
    return {
      phoneCode: parsed.countryCallingCode || normalizedFallback || '91',
      localNumber: normalizeLocalMobileNumber(parsed.nationalNumber),
    }
  }

  const matchedCode = allPhoneCodesDesc.find((code) =>
    normalizedStored.startsWith(code),
  )

  if (matchedCode) {
    const localNumber = normalizedStored.slice(matchedCode.length)
    if (localNumber.length > 0 && localNumber.length <= MOBILE_NUMBER_LOCAL_MAX_DIGITS) {
      return { phoneCode: matchedCode, localNumber }
    }
  }

  if (
    normalizedFallback &&
    normalizedStored.startsWith(normalizedFallback) &&
    normalizedStored.length > normalizedFallback.length
  ) {
    return {
      phoneCode: normalizedFallback,
      localNumber: normalizedStored.slice(normalizedFallback.length),
    }
  }

  return {
    phoneCode: normalizedFallback || '91',
    localNumber: normalizedStored,
  }
}
